/**
 * Карточка каталога со снимком 3D-модели — для разделов, у которых есть объём (мебель,
 * декор, сантехника, электрика, свет). Пока снимок делается или если модель не снялась,
 * в карточке символ с плана. Снимок — готовая картинка рядом с моделью (грузится сразу,
 * ~10 КБ); нет её — снимается в браузере (`useModelThumb`). Карточку ставят кликом или перетаскивают на чертёж;
 * «глазик» в углу открывает модель крупно, с вращением.
 *
 * Карточка типа («Диван · 17 моделей») кликом раскрывает все его модели — выбор делают
 * глазами, а не после установки.
 */
import { useState } from "react";
import type { CatalogItem } from "@/shared/catalog";
import { UIKit } from "@/shared/ui";
import { CatalogItemPreview } from "@/widgets/catalog";
import type { CatalogCardDragProps } from "@/widgets/place-fixture";
import {
	PreviewEyeButton,
	staticThumbUrl,
	useModelPeek,
	useModelThumb,
} from "@/widgets/model-preview";

interface Props {
	/** Элемент каталога целиком: по нему снимок и символ-заглушка */
	item: CatalogItem;
	title: string;
	/** Строка под названием: «17 моделей», размеры */
	meta?: string;
	isActive: boolean;
	onClick: () => void;
	dragProps: CatalogCardDragProps;
	peekProps?: ReturnType<ReturnType<typeof useModelPeek>["peekProps"]>;
	onPreview?: () => void;
}

export const CatalogTile = ({
	item,
	title,
	meta,
	isActive,
	onClick,
	dragProps,
	peekProps,
	onPreview,
}: Props) => {
	const ready = staticThumbUrl(item.view3d);
	// Готовой картинки нет (новая модель) — снимаем модель в браузере
	const [readyMissing, setReadyMissing] = useState(false);
	const showReady = ready !== null && !readyMissing;
	const shot = useModelThumb(showReady ? undefined : item.view3d);
	const thumb = showReady ? ready : shot;
	return (
		<div className="group relative min-w-0" {...peekProps}>
			<button
				type="button"
				title={item.description}
				className={`flex w-full flex-col gap-1 rounded-xl border p-1.5 text-left transition-colors duration-150${
					isActive
						? " border-accent bg-accent-soft"
						: " border-transparent hover:border-border hover:bg-bg"
				}`}
				onClick={onClick}
				{...dragProps}
			>
				<UIKit.Text
					as="span"
					className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg bg-bg"
				>
					{thumb ? (
						<img
							src={thumb}
							alt=""
							draggable={false}
							loading="lazy"
							decoding="async"
							className="size-full object-contain p-1"
							onError={showReady ? () => setReadyMissing(true) : undefined}
						/>
					) : (
						<UIKit.Text
							as="span"
							className={`size-3/4${thumb === undefined ? " animate-pulse" : ""}`}
						>
							<CatalogItemPreview item={item} sizePx={96} />
						</UIKit.Text>
					)}
				</UIKit.Text>
				<UIKit.Text
					as="span"
					className="line-clamp-2 px-0.5 text-xs leading-snug font-bold wrap-break-word text-ink"
				>
					{title}
				</UIKit.Text>
				{meta && (
					<UIKit.Text as="span" className="px-0.5 text-xs leading-none text-ink/60">
						{meta}
					</UIKit.Text>
				)}
			</button>
			{onPreview && <PreviewEyeButton onClick={onPreview} />}
		</div>
	);
};
